import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus, Search, MoreHorizontal, Copy, Archive, ArchiveRestore, Trash2, ExternalLink,
  FileSignature, Send, Check, X, Pencil,
} from "lucide-react";

const STATUS_TABS = [
  { key: "active", label: "Active" },
  { key: "draft", label: "Drafts" },
  { key: "sent", label: "Sent" },
  { key: "under_negotiation", label: "In Negotiation" },
  { key: "approved,project_created", label: "Approved" },
  { key: "rejected,expired", label: "Closed" },
  { key: "archived", label: "Archived" },
];

const PRICING_MODELS = [
  { key: "hourly", label: "Hourly", description: "Charge for hours worked at an agreed hourly rate." },
  { key: "fixed_project", label: "Fixed Project", description: "One agreed price for the entire project." },
  { key: "monthly_resource", label: "Monthly Resource", description: "Retainer of N designers at a monthly rate." },
  { key: "per_drawing_sheet", label: "Per Drawing/Sheet", description: "Charge by the number of drawings/sheets produced." },
  { key: "lump_sum", label: "Lump Sum", description: "Total agreed amount without granular breakdown." },
  { key: "custom", label: "Custom", description: "User-defined pricing with free-text notes." },
];

const STATUS_STYLES = {
  draft: "bg-zinc-100 text-zinc-700",
  sent: "bg-blue-50 text-blue-700 border-blue-200",
  under_negotiation: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  project_created: "bg-emerald-100 text-emerald-800 border-emerald-300",
  rejected: "bg-red-50 text-red-700 border-red-200",
  expired: "bg-zinc-100 text-zinc-500",
};

const INR = (n = 0) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);

const emptyLine = () => ({
  id: `l_${Math.random().toString(36).slice(2, 8)}`,
  description: "", discipline: "architectural", software: [], lod: "300",
  hours: 0, hourly_rate: 800, margin_pct: 20, risk: "medium",
});

// ---------- Create Form (with pricing-model-aware fields) ----------
function QuotationForm({ form, setForm, clients, isEdit = false }) {
  const model = form.pricing_model;
  const setLine = (i, k, v) => {
    const lines = [...(form.lines || [])];
    lines[i] = { ...lines[i], [k]: v };
    setForm({ ...form, lines });
  };
  const addLine = () => setForm({ ...form, lines: [...(form.lines || []), emptyLine()] });
  const removeLine = (i) => setForm({ ...form, lines: form.lines.filter((_, x) => x !== i) });

  const setPricingField = (bucket, k, v) => {
    setForm({ ...form, [bucket]: { ...(form[bucket] || {}), [k]: v } });
  };

  return (
    <div className="mt-6 space-y-6">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Label htmlFor="qt-title">Title</Label>
          <Input id="qt-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="qt-title" />
        </div>
        <div>
          <Label>Client</Label>
          <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
            <SelectTrigger data-testid="qt-client"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="qt-valid">Valid till</Label>
          <Input id="qt-valid" type="date" value={form.valid_till || ""} onChange={(e) => setForm({ ...form, valid_till: e.target.value })} data-testid="qt-valid" />
        </div>
      </div>

      <div>
        <Label>Pricing Model</Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2" data-testid="qt-pricing-models">
          {PRICING_MODELS.map((pm) => (
            <button
              type="button"
              key={pm.key}
              onClick={() => setForm({ ...form, pricing_model: pm.key })}
              className={`text-left p-3 border rounded-md text-xs transition-colors ${
                model === pm.key ? "border-zinc-900 bg-zinc-50" : "border-zinc-200 hover:border-zinc-400"
              }`}
              data-testid={`pm-${pm.key}`}
            >
              <div className="font-semibold text-zinc-900 text-sm">{pm.label}</div>
              <div className="text-zinc-500 mt-1 leading-snug">{pm.description}</div>
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label htmlFor="qt-scope">Scope of Work</Label>
        <Textarea
          id="qt-scope"
          value={form.scope_of_work || ""}
          onChange={(e) => setForm({ ...form, scope_of_work: e.target.value })}
          rows={4}
          placeholder="Describe deliverables, boundaries, exclusions, assumptions…"
          data-testid="qt-scope"
        />
      </div>

      {/* Hourly pricing */}
      {model === "hourly" && (
        <div className="border border-zinc-200 rounded-md" data-testid="hourly-panel">
          <div className="flex items-center justify-between px-4 py-2 bg-zinc-50 border-b border-zinc-100">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Hourly Lines</div>
            <Button type="button" size="sm" variant="outline" onClick={addLine} data-testid="line-add"><Plus className="w-3 h-3 mr-1" />Add</Button>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-white border-b border-zinc-100 text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left px-3 py-2">Description</th>
                <th className="text-left px-3 py-2">Discipline</th>
                <th className="text-right px-3 py-2">Hours</th>
                <th className="text-right px-3 py-2">Rate</th>
                <th className="text-right px-3 py-2">Margin %</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {(form.lines || []).map((l, i) => (
                <tr key={l.id}>
                  <td className="px-3 py-1.5"><Input value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} className="h-8" data-testid={`line-desc-${i}`} /></td>
                  <td className="px-3 py-1.5">
                    <Select value={l.discipline} onValueChange={(v) => setLine(i, "discipline", v)}>
                      <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["architectural","structural","mep","bim","interior","landscape","urban","civil","reality_capture","consulting"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-3 py-1.5"><Input type="number" value={l.hours} onChange={(e) => setLine(i, "hours", e.target.value)} className="h-8 text-right font-mono" data-testid={`line-hrs-${i}`} /></td>
                  <td className="px-3 py-1.5"><Input type="number" value={l.hourly_rate} onChange={(e) => setLine(i, "hourly_rate", e.target.value)} className="h-8 text-right font-mono" /></td>
                  <td className="px-3 py-1.5"><Input type="number" value={l.margin_pct} onChange={(e) => setLine(i, "margin_pct", e.target.value)} className="h-8 text-right font-mono" /></td>
                  <td className="px-3 py-1.5 text-right">
                    <button type="button" onClick={() => removeLine(i)} className="text-zinc-400 hover:text-red-600" data-testid={`line-remove-${i}`}><X className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              ))}
              {!form.lines?.length && (
                <tr><td colSpan={6} className="text-center text-zinc-400 py-4">No lines yet. Add one above.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {model === "fixed_project" && (
        <div className="grid grid-cols-2 gap-3" data-testid="fixed-panel">
          <div>
            <Label>Agreed project price</Label>
            <Input type="number" value={form.fixed?.agreed_price ?? 0} onChange={(e) => setPricingField("fixed", "agreed_price", e.target.value)} className="font-mono" data-testid="fixed-price" />
          </div>
        </div>
      )}

      {model === "monthly_resource" && (
        <div className="grid grid-cols-3 gap-3" data-testid="monthly-panel">
          <div>
            <Label># Resources</Label>
            <Input type="number" value={form.monthly?.num_resources ?? 1} onChange={(e) => setPricingField("monthly", "num_resources", e.target.value)} className="font-mono" data-testid="mo-num" />
          </div>
          <div>
            <Label>Monthly rate</Label>
            <Input type="number" value={form.monthly?.monthly_rate ?? 0} onChange={(e) => setPricingField("monthly", "monthly_rate", e.target.value)} className="font-mono" data-testid="mo-rate" />
          </div>
          <div>
            <Label>Duration (months)</Label>
            <Input type="number" value={form.monthly?.duration_months ?? 1} onChange={(e) => setPricingField("monthly", "duration_months", e.target.value)} className="font-mono" data-testid="mo-dur" />
          </div>
        </div>
      )}

      {model === "per_drawing_sheet" && (
        <div className="grid grid-cols-2 gap-3" data-testid="sheet-panel">
          <div>
            <Label># Sheets</Label>
            <Input type="number" value={form.per_sheet?.num_sheets ?? 0} onChange={(e) => setPricingField("per_sheet", "num_sheets", e.target.value)} className="font-mono" data-testid="sheet-num" />
          </div>
          <div>
            <Label>Rate per sheet</Label>
            <Input type="number" value={form.per_sheet?.rate_per_sheet ?? 0} onChange={(e) => setPricingField("per_sheet", "rate_per_sheet", e.target.value)} className="font-mono" data-testid="sheet-rate" />
          </div>
        </div>
      )}

      {model === "lump_sum" && (
        <div className="grid grid-cols-2 gap-3" data-testid="lump-panel">
          <div>
            <Label>Total agreed amount</Label>
            <Input type="number" value={form.lump_sum?.agreed_price ?? 0} onChange={(e) => setPricingField("lump_sum", "agreed_price", e.target.value)} className="font-mono" data-testid="lump-price" />
          </div>
        </div>
      )}

      {model === "custom" && (
        <div className="space-y-3" data-testid="custom-panel">
          <div>
            <Label>Agreed price</Label>
            <Input type="number" value={form.custom?.agreed_price ?? 0} onChange={(e) => setPricingField("custom", "agreed_price", e.target.value)} className="font-mono" data-testid="custom-price" />
          </div>
          <div>
            <Label>Custom pricing notes</Label>
            <Textarea rows={3} value={form.custom?.notes || ""} onChange={(e) => setPricingField("custom", "notes", e.target.value)} data-testid="custom-notes" />
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Row Delete guard ----------
function DeleteAction({ q, onConfirm }) {
  const [open, setOpen] = useState(false);
  const { data: linked } = useQuery({
    queryKey: ["qt-linked", q.id, open],
    queryFn: async () => (await api.get(`/quotations/${q.id}/linked`)).data,
    enabled: open,
  });
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <button
        onClick={() => setOpen(true)}
        className="w-full text-left px-2 py-1.5 text-sm hover:bg-zinc-100 rounded-sm flex items-center gap-2 text-red-600"
        data-testid={`delete-${q.id}`}
      ><Trash2 className="w-3.5 h-3.5" /> Delete permanently</button>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {q.number}?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div>
              {linked ? (linked.can_delete ? (
                <>This quotation has no downstream project, handover or invoice. Deletion is permanent.</>
              ) : (
                <>
                  <div>This quotation cannot be deleted — it has linked records:</div>
                  <ul className="mt-3 space-y-1 text-xs font-mono">
                    {Object.entries(linked).filter(([k, v]) => !["total", "can_delete"].includes(k) && v > 0).map(([k, v]) => <li key={k}>· {k}: <b>{v}</b></li>)}
                  </ul>
                  <div className="mt-3 text-sm">Archive it instead.</div>
                </>
              )) : "Checking…"}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={!linked?.can_delete}
            onClick={() => { onConfirm(q.id); setOpen(false); }}
            className="bg-red-600 hover:bg-red-700"
            data-testid={`delete-confirm-${q.id}`}
          >Delete permanently</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ---------- Main Page ----------
export default function Quotations() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("active");
  const [search, setSearch] = useState("");
  const [pricingFilter, setPricingFilter] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    title: "", client_id: "", pricing_model: "hourly", currency: "INR",
    tax_percent: 18, scope_of_work: "", lines: [emptyLine()],
    fixed: { agreed_price: 0 }, monthly: { num_resources: 1, monthly_rate: 60000, duration_months: 3 },
    per_sheet: { num_sheets: 10, rate_per_sheet: 2500 }, lump_sum: { agreed_price: 0 },
    custom: { agreed_price: 0, notes: "" },
  });

  const { data: clientsList = [] } = useQuery({ queryKey: ["clients", "active"], queryFn: async () => (await api.get("/clients?status=active")).data });
  const { data: rows = [] } = useQuery({
    queryKey: ["quotations", tab, search, pricingFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("status", tab);
      if (search) params.set("q", search);
      if (pricingFilter) params.set("pricing_model", pricingFilter);
      return (await api.get(`/quotations?${params.toString()}`)).data;
    },
  });

  const createMut = useMutation({
    mutationFn: (payload) => api.post("/quotations", payload),
    onSuccess: () => {
      toast.success("Quotation created");
      setAddOpen(false);
      qc.invalidateQueries({ queryKey: ["quotations"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const editMut = useMutation({
    mutationFn: ({ id, payload }) => api.patch(`/quotations/${id}`, payload),
    onSuccess: () => {
      toast.success("Quotation updated");
      setEditing(null);
      qc.invalidateQueries({ queryKey: ["quotations"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const rowActionMut = useMutation({
    mutationFn: ({ id, action, body = {} }) => api.post(`/quotations/${id}/${action}`, body),
    onSuccess: (_, vars) => {
      toast.success(`${vars.action.replace(/_/g, " ")} · done`);
      qc.invalidateQueries({ queryKey: ["quotations"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const deleteMut = useMutation({
    mutationFn: (id) => api.delete(`/quotations/${id}`),
    onSuccess: () => { toast.success("Quotation deleted"); qc.invalidateQueries({ queryKey: ["quotations"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const submitCreate = (e) => {
    e.preventDefault();
    if (!form.client_id) return toast.error("Please pick a client");
    if (!form.title) return toast.error("Title is required");
    const payload = { ...form };
    // Send only lines for hourly; leave the rest untouched
    createMut.mutate(payload);
  };
  const submitEdit = (e) => {
    e.preventDefault();
    editMut.mutate({ id: editing.id, payload: form });
  };

  const openEdit = (q) => {
    setEditing(q);
    setForm({
      title: q.title || "",
      client_id: q.client_id,
      pricing_model: q.pricing_model || "hourly",
      currency: q.currency || "INR",
      tax_percent: q.tax_percent ?? 18,
      valid_till: q.valid_till || "",
      scope_of_work: q.scope_of_work || "",
      lines: q.lines?.length ? q.lines : [emptyLine()],
      fixed: q.fixed || { agreed_price: 0 },
      monthly: q.monthly || { num_resources: 1, monthly_rate: 60000, duration_months: 3 },
      per_sheet: q.per_sheet || { num_sheets: 10, rate_per_sheet: 2500 },
      lump_sum: q.lump_sum || { agreed_price: 0 },
      custom: q.custom || { agreed_price: 0, notes: "" },
    });
  };

  const canEdit = (q) => ["draft", "sent", "under_negotiation"].includes(q.status) && !q.archived;
  const canSend = (q) => ["draft", "under_negotiation"].includes(q.status) && !q.archived;
  const canApprove = (q) => ["draft", "sent", "under_negotiation"].includes(q.status) && !q.archived;

  return (
    <div data-testid="quotations-page">
      <PageHeader
        eyebrow="Business Division · Revenue"
        title="Quotations"
        description="Every revenue conversation lives here — from draft through negotiation to approval. On approval a Project + Handover is auto-created."
        actions={
          <Sheet open={addOpen} onOpenChange={setAddOpen}>
            <SheetTrigger asChild>
              <Button data-testid="new-quotation-btn"><Plus className="w-4 h-4 mr-1.5" /> New Quotation</Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto">
              <SheetHeader><SheetTitle>New Quotation</SheetTitle></SheetHeader>
              <form onSubmit={submitCreate}>
                <QuotationForm form={form} setForm={setForm} clients={clientsList} />
                <SheetFooter className="mt-6">
                  <Button type="submit" disabled={createMut.isPending} data-testid="qt-submit">
                    {createMut.isPending ? "Creating…" : "Create as Draft"}
                  </Button>
                </SheetFooter>
              </form>
            </SheetContent>
          </Sheet>
        }
      />

      <div className="max-w-[1400px] mx-auto px-8 py-6 space-y-4">
        {/* Tabs + Filters */}
        <div className="flex flex-wrap items-center gap-1 border-b border-zinc-200" data-testid="qt-status-tabs">
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${tab === t.key ? "border-zinc-900 text-zinc-900 font-medium" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}
              data-testid={`tab-${t.key}`}
            >{t.label}</button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input placeholder="Search by number, title or client…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 h-9" data-testid="qt-search" />
          </div>
          <Select value={pricingFilter || "all"} onValueChange={(v) => setPricingFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="h-9 w-56" data-testid="qt-filter-model"><SelectValue placeholder="All pricing models" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All pricing models</SelectItem>
              {PRICING_MODELS.map((pm) => <SelectItem key={pm.key} value={pm.key}>{pm.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-xs text-zinc-500 font-mono ml-auto" data-testid="qt-count">{rows.length} row{rows.length === 1 ? "" : "s"}</span>
        </div>

        {/* Table */}
        <div className="border border-zinc-200 rounded-lg bg-white overflow-hidden" data-testid="qt-table">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left px-4 py-3">Number</th>
                <th className="text-left px-4 py-3">Client · Title</th>
                <th className="text-left px-4 py-3">Model</th>
                <th className="text-right px-4 py-3">Price</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {rows.map((q) => (
                <tr key={q.id} data-testid={`qt-row-${q.id}`} className={q.archived ? "opacity-60" : ""}>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-700">{q.number}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-zinc-900">{q.title}</div>
                    <div className="text-[11px] text-zinc-500">{q.client_name || "—"}</div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className="capitalize text-[10px]">{(q.pricing_model || "hourly").replace(/_/g, " ")}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-xs">{(() => {
                    const p = q.negotiated_price ?? q.suggested_price ?? 0;
                    return p > 0 ? INR(p) : <span className="text-zinc-400">—</span>;
                  })()}</td>
                  <td className="px-4 py-3">
                    <Badge className={`capitalize text-[10px] border ${STATUS_STYLES[q.status] || ""}`} data-testid={`status-${q.id}`}>
                      {(q.status || "").replace(/_/g, " ")}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Link to={`/app/quotations/${q.id}`} className="text-xs text-zinc-700 hover:text-zinc-900 inline-flex items-center gap-1" data-testid={`qt-open-${q.id}`}>
                        Open <ExternalLink className="w-3 h-3" />
                      </Link>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-1.5 rounded-sm hover:bg-zinc-100" data-testid={`row-menu-${q.id}`} aria-label="Row actions">
                            <MoreHorizontal className="w-4 h-4 text-zinc-500" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                          {canEdit(q) && (
                            <DropdownMenuItem onSelect={() => openEdit(q)} data-testid={`edit-${q.id}`}>
                              <Pencil className="w-3.5 h-3.5 mr-2" /> Edit
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onSelect={() => rowActionMut.mutate({ id: q.id, action: "duplicate" })} data-testid={`dup-${q.id}`}>
                            <Copy className="w-3.5 h-3.5 mr-2" /> Duplicate
                          </DropdownMenuItem>
                          {canSend(q) && (
                            <DropdownMenuItem onSelect={() => rowActionMut.mutate({ id: q.id, action: "send" })} data-testid={`send-${q.id}`}>
                              <Send className="w-3.5 h-3.5 mr-2" /> Send to client
                            </DropdownMenuItem>
                          )}
                          {canApprove(q) && (
                            <DropdownMenuItem onSelect={() => rowActionMut.mutate({ id: q.id, action: "approve" })} data-testid={`approve-${q.id}`}>
                              <Check className="w-3.5 h-3.5 mr-2" /> Approve
                            </DropdownMenuItem>
                          )}
                          {canApprove(q) && (
                            <DropdownMenuItem onSelect={() => rowActionMut.mutate({ id: q.id, action: "reject", body: { reason: "Rejected via list" } })} data-testid={`reject-${q.id}`}>
                              <X className="w-3.5 h-3.5 mr-2" /> Reject
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          {q.archived ? (
                            <DropdownMenuItem onSelect={() => rowActionMut.mutate({ id: q.id, action: "unarchive" })} data-testid={`unarchive-${q.id}`}>
                              <ArchiveRestore className="w-3.5 h-3.5 mr-2" /> Restore
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onSelect={() => rowActionMut.mutate({ id: q.id, action: "archive" })} data-testid={`archive-${q.id}`}>
                              <Archive className="w-3.5 h-3.5 mr-2" /> Archive
                            </DropdownMenuItem>
                          )}
                          <div className="p-0"><DeleteAction q={q} onConfirm={(id) => deleteMut.mutate(id)} /></div>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-zinc-500" data-testid="qt-empty">No quotations match this filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit sheet */}
      <Sheet open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Edit {editing?.number}</SheetTitle>
          </SheetHeader>
          <form onSubmit={submitEdit}>
            <QuotationForm form={form} setForm={setForm} clients={clientsList} isEdit />
            <SheetFooter className="mt-6">
              <Button type="submit" disabled={editMut.isPending} data-testid="edit-submit">Save changes</Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  );
}
